import ColumnMenuHost from './ColumnMenuHost';
import React, { useEffect, useRef, useState } from 'react';
import { NtIcon } from '../NotionUI';
import Dropdown from './Dropdown';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  TICKETS_COLUMN,
  TYPE_ICONS,
  GROUPABLE_TYPES,
  NO_VALUE_OPS,
  opsForColumn,
  opFamily,
  ticketsPseudoColumn,
  allColumnsInOrder,
} from '../../utils/projectView';

const PANEL_WIDTH = 420;

const personLabel = (u) => [u.name, u.surname].filter(Boolean).join(' ');

/** Campo del valore di una regola di filtro, in base al tipo di colonna. */
function RuleValue({ rule, column, users, onChange }) {
  const { t } = useLanguage();
  if (!column || NO_VALUE_OPS.includes(rule.op)) return null;
  const family = opFamily(column);

  if (family === 'select' || family === 'multi_select') {
    return (
      <Dropdown
        value={rule.value ?? ''}
        onChange={onChange}
        ariaLabel={t('projectView.filterValue')}
        options={[{ value: '', label: '—' }, ...column.options.map((o) => ({ value: o.value, label: o.label }))]}
      />
    );
  }
  if (family === 'person') {
    return (
      <Dropdown
        value={rule.value ?? ''}
        onChange={onChange}
        ariaLabel={t('projectView.filterValue')}
        options={[{ value: '', label: '—' }, { value: 'me', label: t('projectView.me') }, ...users.map((u) => ({ value: u.id, label: personLabel(u) }))]}
      />
    );
  }
  if (family === 'date' || family === 'daterange') {
    const isToday = rule.value === 'today';
    return (
      <span className="pf-date">
        <Dropdown
          value={isToday ? 'today' : 'date'}
          onChange={(v) => onChange(v === 'today' ? 'today' : '')}
          ariaLabel={t('projectView.filterValue')}
          options={[{ value: 'date', label: t('projectView.specificDate') }, { value: 'today', label: t('projectView.today') }]}
        />
        {!isToday && <input type="date" value={rule.value ?? ''} onChange={(e) => onChange(e.target.value)} />}
      </span>
    );
  }
  return (
    <input
      type={family === 'number' ? 'number' : 'text'}
      step="any"
      value={rule.value ?? ''}
      placeholder={t('projectView.filterValue')}
      aria-label={t('projectView.filterValue')}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FilterPanel({ columns, users, config, onConfig }) {
  const { t } = useLanguage();
  const filter = config.filter || { mode: 'and', rules: [] };
  const rules = filter.rules || [];
  const all = [...columns, ticketsPseudoColumn(t('projectView.linkedTickets'))];
  const byId = Object.fromEntries(all.map((c) => [String(c.id), c]));

  const setRules = (next, mode = filter.mode || 'and') => onConfig({ filter: { mode, rules: next } });
  const patchRule = (index, patch) => setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addRule = () => {
    const column = columns[0];
    setRules([...rules, { column: column.id, op: opsForColumn(column)[0], value: '' }]);
  };

  return (
    <div className="pv-panel">
      {rules.length === 0 && <p className="nt-muted pv-empty">{t('projectView.noFilters')}</p>}
      {rules.map((rule, index) => {
        const column = byId[String(rule.column)];
        return (
          <div key={index} className="pf-rule">
            <Dropdown
              value={rule.column}
              ariaLabel={t('projectView.property')}
              options={all.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(v) => {
                const next = byId[String(v)];
                patchRule(index, { column: next.id === TICKETS_COLUMN ? TICKETS_COLUMN : next.id, op: opsForColumn(next)[0], value: '' });
              }}
            />
            <Dropdown
              value={rule.op}
              ariaLabel={t('projectView.operator')}
              options={(column ? opsForColumn(column) : []).map((op) => ({ value: op, label: t(`projectView.op_${op}`) }))}
              onChange={(op) => patchRule(index, { op, value: NO_VALUE_OPS.includes(op) ? '' : rule.value })}
            />
            <RuleValue rule={rule} column={column} users={users} onChange={(value) => patchRule(index, { value })} />
            <button type="button" className="pd-row-delete pd-option-remove" aria-label={t('projectView.removeRule')} onClick={() => setRules(rules.filter((_, i) => i !== index))}>×</button>
          </div>
        );
      })}
      {rules.length > 1 && (
        <Dropdown
          className="pf-mode"
          value={filter.mode || 'and'}
          onChange={(mode) => setRules(rules, mode)}
          ariaLabel={t('projectView.matchMode')}
          options={[{ value: 'and', label: t('projectView.matchAll') }, { value: 'or', label: t('projectView.matchAny') }]}
        />
      )}
      <div className="pv-panel-actions">
        <button type="button" className="nt-ghost-btn" onClick={addRule}>+ {t('projectView.addFilter')}</button>
        {rules.length > 0 && <button type="button" className="nt-ghost-btn" onClick={() => onConfig({ filter: { mode: 'and', rules: [] } })}>{t('projectView.clearFilters')}</button>}
      </div>
    </div>
  );
}

function SortPanel({ columns, config, onConfig }) {
  const { t } = useLanguage();
  const sorts = config.sorts || [];
  const setSorts = (next) => onConfig({ sorts: next });

  return (
    <div className="pv-panel">
      {sorts.length === 0 && <p className="nt-muted pv-empty">{t('projectView.noSorts')}</p>}
      {sorts.map((sort, index) => (
        <div key={index} className="pf-rule">
          <Dropdown
            value={sort.column}
            ariaLabel={t('projectView.property')}
            options={columns.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(v) => setSorts(sorts.map((s, i) => (i === index ? { ...s, column: Number(v) } : s)))}
          />
          <Dropdown
            value={sort.dir}
            ariaLabel={t('projectView.direction')}
            options={[{ value: 'asc', label: t('projectView.ascending') }, { value: 'desc', label: t('projectView.descending') }]}
            onChange={(dir) => setSorts(sorts.map((s, i) => (i === index ? { ...s, dir } : s)))}
          />
          <button type="button" className="pd-row-delete pd-option-remove" aria-label={t('projectView.removeRule')} onClick={() => setSorts(sorts.filter((_, i) => i !== index))}>×</button>
        </div>
      ))}
      <div className="pv-panel-actions">
        <button type="button" className="nt-ghost-btn" onClick={() => setSorts([...sorts, { column: columns[0].id, dir: 'asc' }])}>+ {t('projectView.addSort')}</button>
        {sorts.length > 0 && <button type="button" className="nt-ghost-btn" onClick={() => setSorts([])}>{t('projectView.clearSorts')}</button>}
      </div>
    </div>
  );
}

function PropertiesPanel({ columns, config, onConfig, onColumnSubmit, onColumnAction }) {
  const { t } = useLanguage();
  const [dragId, setDragId] = useState(null);
  const [menuId, setMenuId] = useState(null); // colonna di cui è aperto il menu
  const ordered = allColumnsInOrder(columns, config);
  const hidden = config.hidden || [];
  const movable = ordered.filter((c) => c.type !== 'title');

  const toggle = (id) => onConfig({ hidden: hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id] });
  const drop = (targetId) => {
    if (dragId === null || dragId === targetId) return;
    const ids = movable.map((c) => c.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    onConfig({ order: ids });
    setDragId(null);
  };

  return (
    <div className="pv-panel">
      {ordered.map((column) => (
        <div
          key={column.id}
          className="pp-row"
          draggable={column.type !== 'title'}
          onDragStart={() => setDragId(column.id)}
          onDragOver={(e) => column.type !== 'title' && e.preventDefault()}
          onDrop={() => drop(column.id)}
        >
          <span className="pp-grip nt-muted">{column.type !== 'title' && <NtIcon name="grip" />}</span>
          <span className="nt-muted"><NtIcon name={TYPE_ICONS[column.type]} /></span>
          <span className="pp-name">{column.name}</span>
          {onColumnSubmit && (
            <button
              type="button"
              className="pd-row-delete pd-option-remove"
              aria-label={t('projectView.columnMenu', { name: column.name })}
              title={t('projectView.columnMenu', { name: column.name })}
              onClick={() => setMenuId(column.id)}
            >
              <NtIcon name="more" />
            </button>
          )}
          <button
            type="button"
            className="pd-row-delete pd-option-remove"
            disabled={column.type === 'title'}
            aria-label={hidden.includes(column.id) ? t('projectView.showColumn') : t('projectView.hideColumn')}
            onClick={() => toggle(column.id)}
          >
            <NtIcon name={hidden.includes(column.id) ? 'eyeoff' : 'eye'} />
          </button>
          {menuId === column.id && (
            <ColumnMenuHost
              column={column}
              onClose={() => setMenuId(null)}
              onColumnSubmit={onColumnSubmit}
              onColumnAction={onColumnAction}
            />
          )}
        </div>
      ))}
      <div className="pv-panel-actions">
        <button type="button" className="nt-ghost-btn" onClick={() => onConfig({ hidden: movable.map((c) => c.id) })}>{t('projectView.hideAll')}</button>
        <button type="button" className="nt-ghost-btn" onClick={() => onConfig({ hidden: [] })}>{t('projectView.showAll')}</button>
      </div>
    </div>
  );
}

function GroupPanel({ columns, config, onConfig, required }) {
  const { t } = useLanguage();
  const groupable = columns.filter((c) => GROUPABLE_TYPES.includes(c.type));
  return (
    <div className="pv-panel">
      <label className="pd-popover-label">{t('projectView.groupBy')}</label>
      <Dropdown
        value={config.group || ''}
        ariaLabel={t('projectView.groupBy')}
        options={[...(required ? [] : [{ value: '', label: t('projectView.noGrouping') }]), ...groupable.map((c) => ({ value: c.id, label: c.name }))]}
        onChange={(v) => onConfig({ group: v ? Number(v) : null })}
      />
    </div>
  );
}

/**
 * Barra degli strumenti della vista: filtro, ordinamento, raggruppamento,
 * proprietà (visibilità e ordine delle colonne) e ricerca. Ogni modifica
 * passa da `onConfig(patch)` e viene salvata nella vista.
 */
function ViewToolbar({ viewType, columns, users, config, onConfig, onColumnSubmit, onColumnAction, search, onSearch, forceOpen }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(null);
  const [anchor, setAnchor] = useState({ top: 0, left: 0 }); // posizione del pannello aperto (fixed, dentro lo schermo)
  const buttons = useRef({});
  const place = (rect) => setAnchor({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)) });
  const [searching, setSearching] = useState(Boolean(search));

  // Il padre può aprire un pannello (es. "Filtra" dal menu di una colonna).
  useEffect(() => {
    if (!forceOpen) return;
    const el = buttons.current[forceOpen.panel];
    if (el) place(el.getBoundingClientRect());
    setOpen(forceOpen.panel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  const filterCount = (config.filter?.rules || []).length;
  const sortCount = (config.sorts || []).length;
  const hiddenCount = (config.hidden || []).length;
  const hasGroupable = columns.some((c) => GROUPABLE_TYPES.includes(c.type));

  const tool = (key, icon, label, count, panel) => (
    <span className="pv-tool" key={key}>
      <button
        type="button"
        className={'nt-ghost-btn pv-tool-btn' + (count ? ' pv-tool-active' : '')}
        aria-expanded={open === key}
        ref={(el) => { buttons.current[key] = el; }}
        onClick={(e) => {
          place(e.currentTarget.getBoundingClientRect());
          setOpen(open === key ? null : key);
        }}
        title={label}
      >
        <NtIcon name={icon} /> <span className="pv-tool-label">{label}</span>{count ? <span className="pv-badge">{count}</span> : null}
      </button>
      {open === key && (
        <>
          <div className="pd-overlay" onClick={() => setOpen(null)} />
          <div className="pd-popover pv-popover" style={{ position: 'fixed', top: anchor.top, left: anchor.left }}>{panel}</div>
        </>
      )}
    </span>
  );

  return (
    <div className="pv-toolbar">
      {tool('filter', 'filter', t('projectView.filter'), filterCount, <FilterPanel columns={columns} users={users} config={config} onConfig={onConfig} />)}
      {viewType !== 'gantt' && tool('sort', 'sort', t('projectView.sort'), sortCount, <SortPanel columns={columns} config={config} onConfig={onConfig} />)}
      {(viewType === 'table' || viewType === 'board' || viewType === 'list') && hasGroupable &&
        tool('group', 'layers', t('projectView.group'), config.group ? 1 : 0, <GroupPanel columns={columns} config={config} onConfig={onConfig} required={viewType === 'board'} />)}
      {(viewType === 'table' || viewType === 'list' || viewType === 'gallery') && tool('props', 'eye', t('projectView.properties'), hiddenCount, <PropertiesPanel columns={columns} config={config} onConfig={onConfig} onColumnSubmit={onColumnSubmit} onColumnAction={onColumnAction} />)}
      {searching ? (
        <span className="pv-search">
          <NtIcon name="search" />
          <input
            autoFocus
            value={search}
            placeholder={t('projectView.searchPlaceholder')}
            aria-label={t('projectView.search')}
            onChange={(e) => onSearch(e.target.value)}
            onBlur={() => !search && setSearching(false)}
            onKeyDown={(e) => e.key === 'Escape' && (onSearch(''), setSearching(false))}
          />
        </span>
      ) : (
        <button type="button" className="nt-ghost-btn pv-tool-btn" onClick={() => setSearching(true)} title={t('projectView.search')}>
          <NtIcon name="search" /> <span className="pv-tool-label">{t('projectView.search')}</span>
        </button>
      )}
    </div>
  );
}

export default ViewToolbar;
